import * as React from "react";
import DashboardLayout from "../layouts/DashboardLayout";
import { UserAuth } from "../context/AuthContext";
import { useParams } from "react-router-dom";
import Link from "@mui/joy/Link";
import Typography from "@mui/joy/Typography";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import { toast } from "react-toastify";
import { db } from "../FirebaseConfig";
import DownloadIcon from "@mui/icons-material/Download";
import EventDetails from "../components/EventDetail";
import { useNavigate } from "react-router-dom";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
interface RouteParams {
  [key: string]: string | undefined;
  id: string;
}

function parseSlug(slug: any): any {
  return slug
    .split("-")
    .map((word: any) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export default function EventDetailPage() {
  const [isRegistered, setIsRegistered] = React.useState(false);
  const { userData } = UserAuth();
  const { id } = useParams<RouteParams>();
  const [fileURL, setFileURL] = React.useState<string>("");
  const navigate = useNavigate();
  const [data, setData] = React.useState<any>({});
  const [loading, setLoading] = React.useState(false);
  const [submittedChallenges, setSubmittedChallenges] = React.useState<
    string[]
  >([]);
  const [totalScore, setTotalScore] = React.useState<number>(0);

  React.useEffect(() => {
    const checkRegistration = async () => {
      if (userData?.id && id) {
        try {
          const registrationDoc = await db
            .collection("Events")
            .doc(parseSlug(id).toLowerCase())
            .collection("Registrations")
            .doc(userData?.id)
            .get();

          setIsRegistered(registrationDoc.exists);

          if (!registrationDoc.exists) {
            navigate("/unauthorized");
          }
        } catch (error) {
          toast.error("Could not check registration status.");
        }
      }
    };

    checkRegistration();
  }, [userData, id]);

  React.useEffect(() => {
    const getData = async () => {
      setLoading(true);
      try {
        const eventData = await db
          .collection("Events")
          .doc(parseSlug(id).toLowerCase())
          .get();
        if (!eventData.exists) {
          setLoading(false);
        } else {
          setData(eventData.data());
          setLoading(false);
        }
      } catch (error) {
        setLoading(false);
      }
    };
    getData();
  }, [parseSlug(id)]);

  React.useEffect(() => {
    const fetchSubmittedChallenges = async () => {
      if (userData) {
        const eventDocRef = doc(db, "Events", parseSlug(id).toLowerCase());
        const submissionDocRef = doc(eventDocRef, "Submissions", userData?.id);

        // Listen to changes on registration document in real-time
        const registrationRef = doc(
          db,
          `Events/${parseSlug(id).toLowerCase()}/Registrations`,
          userData?.id
        );

        const unsubscribe = onSnapshot(registrationRef, (registrationDoc) => {
          setTotalScore(registrationDoc?.data()?.points ?? 0);
        });

        // Fetch submissions
        const submissionDoc = await getDoc(submissionDocRef);
        if (submissionDoc.exists()) {
          const submissions = submissionDoc.data().submissions || [];
          const submittedIds = submissions.map(
            (submission: any) => submission.challengeId
          );
          setSubmittedChallenges(submittedIds);
        }

        // Clean up the subscription when the component unmounts
        return () => unsubscribe();
      }
    };

    fetchSubmittedChallenges();
  }, [userData, data.eventname]);

  const hasEventStarted = () => {
    if (!data.startdate) {
      return false;
    }
    const currentDate = new Date();
    const eventStartDate = new Date(data.startdate);
    return currentDate >= eventStartDate;
  };

  return (
    <DashboardLayout
      title={data.eventname}
      navigator={
        <Link underline="none" color="neutral" aria-label="Home">
          <Typography fontWeight={500} fontSize={12}>
            Events
          </Typography>
        </Link>
      }
      visible={true}
      cBox={true}
      data={
        data.category === "CTF" &&
        hasEventStarted() && (
          <Typography color="primary" level="h3" sx={{ marginRight: "0.3rem" }}>
            Total Score: {totalScore}
          </Typography>
        )
      }
      button={
        fileURL && (
          <Box display="flex" alignItems="center">
            <Button
              variant="outlined"
              color="primary"
              startDecorator={<DownloadIcon />}
              sx={{ mr: 1 }}
              onClick={() => {
                window.open(fileURL, "_blank");
              }}
            >
              Download VPN
            </Button>
          </Box>
        )
      }
    >
      {isRegistered && <EventDetails />}
    </DashboardLayout>
  );
}
